import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, User, Phone, Mail, DollarSign, Package, MessageSquare, Filter, X, FileText, Send } from 'lucide-react';
import { getAllBookings, updateBookingStatus, generateReceipt, sendReceiptEmail } from '../utils/api';
import { formatTime12Hour } from '../utils/timeFormat';

const DriverDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [filterStatus, setFilterStatus] = useState('upcoming');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    scheduled: 0,
    driverAssigned: 0,
    activeTrips: 0,
    completedTrips: 0,
    today: 0,
    upcoming: 0
  });

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    applyFilters();
    calculateStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, filterStatus]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const data = await getAllBookings();
      
      // Sort by date and time (earliest first)
      const sorted = data.sort((a, b) => {
        const dateA = new Date(`${a.date} ${a.time}`);
        const dateB = new Date(`${b.date} ${b.time}`);
        return dateA - dateB;
      });
      
      setBookings(sorted);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    
    const scheduled = bookings.filter(b => b.status === 'Scheduled').length;
    const driverAssigned = bookings.filter(b => b.status === 'Driver Assigned').length;
    
    // Active trips: all statuses between Driver En Route and Arrived at Destination
    const activeStatuses = ['Driver En Route', 'Arrived at Pickup', 'Passenger Picked Up', 'En Route to Destination', 'Arrived at Destination'];
    const activeTrips = bookings.filter(b => activeStatuses.includes(b.status)).length;
    
    const completedTrips = bookings.filter(b => b.status === 'Completed' || b.status === 'Receipt Sent').length;
    const todayRides = bookings.filter(b => b.date === today).length;
    const upcoming = bookings.filter(b => {
      const rideDate = new Date(b.date);
      return rideDate >= now;
    }).length;
    
    setStats({
      scheduled,
      driverAssigned,
      activeTrips,
      completedTrips,
      today: todayRides,
      upcoming
    });
  };

  const applyFilters = () => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    
    let filtered = bookings;
    
    switch (filterStatus) {
      case 'scheduled':
        filtered = bookings.filter(b => b.status === 'Scheduled');
        break;
      case 'assigned':
        filtered = bookings.filter(b => b.status === 'Driver Assigned');
        break;
      case 'active':
        {
          const activeStatuses = ['Driver En Route', 'Arrived at Pickup', 'Passenger Picked Up', 'En Route to Destination', 'Arrived at Destination'];
          filtered = bookings.filter(b => activeStatuses.includes(b.status));
        }
        break;
      case 'completed':
        filtered = bookings.filter(b => b.status === 'Completed' || b.status === 'Receipt Sent');
        break;
      case 'today':
        filtered = bookings.filter(b => b.date === today);
        break;
      case 'all':
        filtered = bookings;
        break;
      case 'upcoming':
      default:
        filtered = bookings.filter(b => {
          const rideDate = new Date(`${b.date} ${b.time}`);
          const validStatuses = ['Scheduled', 'Driver Assigned', 'Driver En Route', 'Arrived at Pickup', 'Passenger Picked Up', 'En Route to Destination', 'Arrived at Destination'];
          return rideDate >= now && validStatuses.includes(b.status);
        });
        break;
    }
    
    setFilteredBookings(filtered);
  };

  const handleAssignDriver = async (bookingId) => {
    try {
      await updateBookingStatus(bookingId, 'Driver Assigned');
      await fetchBookings();
      
      // Update selected booking if open
      if (selectedBooking && selectedBooking.id === bookingId) {
        const updated = await getAllBookings();
        const updatedBooking = updated.find(b => b.id === bookingId);
        setSelectedBooking(updatedBooking);
      }
    } catch (error) {
      console.error('Error assigning driver:', error);
      alert('Failed to assign driver');
    }
  };

  const handleStatusTransition = async (bookingId, newStatus) => {
    try {
      await updateBookingStatus(bookingId, newStatus);
      await fetchBookings();
      
      // Update selected booking if open
      if (selectedBooking && selectedBooking.id === bookingId) {
        const updated = await getAllBookings();
        const updatedBooking = updated.find(b => b.id === bookingId);
        setSelectedBooking(updatedBooking);
      }
    } catch (error) {
      console.error(`Error updating status to ${newStatus}:`, error);
      alert(`Failed to update status to ${newStatus}`);
    }
  };

  const handleStartTrip = async (bookingId) => {
    await handleStatusTransition(bookingId, 'Driver En Route');
  };

  const handleGenerateReceipt = async (bookingId) => {
    try {
      const updated = await generateReceipt(bookingId);
      await fetchBookings();
      
      // Update selected booking if open
      if (selectedBooking && selectedBooking.id === bookingId) {
        setSelectedBooking(updated);
      }
      
      alert('Receipt generated successfully!');
    } catch (error) {
      console.error('Error generating receipt:', error);
      alert('Failed to generate receipt. Please try again.');
    }
  };

  const handleSendReceipt = async (bookingId) => {
    try {
      const updated = await sendReceiptEmail(bookingId);
      await fetchBookings();
      
      // Update selected booking if open
      if (selectedBooking && selectedBooking.id === bookingId) {
        setSelectedBooking(updated);
      }
      
      alert('Receipt sent successfully!');
    } catch (error) {
      console.error('Error sending receipt:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to send receipt';
      alert(errorMsg);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'Driver Assigned':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Driver En Route':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'Arrived at Pickup':
        return 'bg-indigo-100 text-indigo-800 border-indigo-300';
      case 'Passenger Picked Up':
        return 'bg-cyan-100 text-cyan-800 border-cyan-300';
      case 'En Route to Destination':
        return 'bg-teal-100 text-teal-800 border-teal-300';
      case 'Arrived at Destination':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'Completed':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'Receipt Sent':
        return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto"></div>
            <p className="mt-4">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Driver Dashboard
          </h1>
          <p className="text-xl text-gray-400">
            View scheduled bookings, open ride details, and prepare trips
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 p-6 rounded-xl shadow-lg">
            <p className="text-yellow-100 text-sm mb-1">Scheduled</p>
            <p className="text-4xl font-bold text-white">{stats.scheduled}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl shadow-lg">
            <p className="text-blue-100 text-sm mb-1">Driver Assigned</p>
            <p className="text-4xl font-bold text-white">{stats.driverAssigned}</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-xl shadow-lg">
            <p className="text-purple-100 text-sm mb-1">Active Trips</p>
            <p className="text-4xl font-bold text-white">{stats.activeTrips}</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-xl shadow-lg">
            <p className="text-green-100 text-sm mb-1">Completed</p>
            <p className="text-4xl font-bold text-white">{stats.completedTrips}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-gray-800 p-4 rounded-xl mb-6 border border-gray-700">
          <div className="flex items-center space-x-2 mb-3">
            <Filter className="w-5 h-5 text-cyan-400" />
            <span className="text-white font-semibold">Filter Bookings:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterStatus('upcoming')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filterStatus === 'upcoming'
                  ? 'bg-cyan-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Upcoming
            </button>
            <button
              onClick={() => setFilterStatus('active')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filterStatus === 'active'
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Active Trips
            </button>
            <button
              onClick={() => setFilterStatus('completed')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filterStatus === 'completed'
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Completed
            </button>
            <button
              onClick={() => setFilterStatus('today')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filterStatus === 'today'
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filterStatus === 'all'
                  ? 'bg-gray-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              All
            </button>
          </div>
        </div>

        {/* Booking List */}
        <div className="space-y-4">
          {filteredBookings.length === 0 ? (
            <div className="bg-gray-800 p-12 rounded-xl text-center border border-gray-700">
              <p className="text-gray-400 text-lg">No bookings found for selected filter</p>
            </div>
          ) : (
            filteredBookings.map((booking) => (
              <div
                key={booking.id}
                className="bg-gray-800 p-6 rounded-xl border border-gray-700 hover:border-cyan-500 transition-all shadow-lg"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  {/* Booking Info */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <span className="text-cyan-400 font-bold text-lg">#{booking.id}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(booking.status)}`}>
                        {booking.status}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center space-x-2 text-white mb-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="font-semibold">{booking.passengerName}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-gray-300 text-sm">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <span>{booking.date}</span>
                          <Clock className="w-4 h-4 text-gray-500 ml-3" />
                          <span>{formatTime12Hour(booking.time)}</span>
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex items-center space-x-2 text-white mb-2">
                          <MapPin className="w-4 h-4 text-green-400" />
                          <span className="text-sm">{booking.pickupLocation}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-white">
                          <MapPin className="w-4 h-4 text-red-400" />
                          <span className="text-sm">{booking.dropoffLocation}</span>
                        </div>
                      </div>
                    </div>
                    
                    {booking.estimatedFare && (
                      <div className="mt-3 flex items-center space-x-2">
                        <DollarSign className="w-5 h-5 text-green-400" />
                        <span className="text-white font-bold">${booking.estimatedFare.toFixed(2)} BSD</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col space-y-2 md:ml-4">
                    <button
                      onClick={() => setSelectedBooking(booking)}
                      className="bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-2 rounded-lg font-semibold transition-all"
                    >
                      Open Booking
                    </button>
                    {booking.status === 'Scheduled' && (
                      <button
                        onClick={() => handleAssignDriver(booking.id)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold transition-all"
                      >
                        Assign to Driver
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Booking Details Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-gray-900 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border-2 border-cyan-500">
            <div className="sticky top-0 bg-gradient-to-r from-cyan-600 to-blue-600 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">Booking Details</h2>
              <button
                onClick={() => setSelectedBooking(null)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Booking Number & Status */}
              <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Booking Number</p>
                    <p className="text-cyan-400 font-bold text-2xl">{selectedBooking.id}</p>
                  </div>
                  <span className={`px-4 py-2 rounded-full text-sm font-bold border ${getStatusColor(selectedBooking.status)}`}>
                    {selectedBooking.status}
                  </span>
                </div>
              </div>

              {/* Customer Information */}
              <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
                <h3 className="text-white font-bold text-lg mb-4 flex items-center">
                  <User className="w-5 h-5 text-cyan-400 mr-2" />
                  Customer Information
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-gray-400 text-sm">Full Name</p>
                    <p className="text-white font-semibold">{selectedBooking.passengerName}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <p className="text-white">{selectedBooking.passengerPhone}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <p className="text-white">{selectedBooking.passengerEmail}</p>
                  </div>
                </div>
              </div>

              {/* Trip Details */}
              <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
                <h3 className="text-white font-bold text-lg mb-4 flex items-center">
                  <MapPin className="w-5 h-5 text-cyan-400 mr-2" />
                  Trip Details
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Pickup Zone</p>
                      <p className="text-white font-semibold">{selectedBooking.pickupZone}</p>
                      <p className="text-gray-300 text-sm">{selectedBooking.pickupLocation}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Dropoff Zone</p>
                      <p className="text-white font-semibold">{selectedBooking.dropoffZone}</p>
                      <p className="text-gray-300 text-sm">{selectedBooking.dropoffLocation}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-700">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Date</p>
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-cyan-400" />
                        <p className="text-white font-semibold">{selectedBooking.date}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Pickup Time</p>
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-cyan-400" />
                        <p className="text-white font-semibold">{formatTime12Hour(selectedBooking.time)}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-700">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Passengers</p>
                      <p className="text-white font-semibold">{selectedBooking.passengers}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Bags</p>
                      <div className="flex items-center space-x-1">
                        <Package className="w-4 h-4 text-cyan-400" />
                        <p className="text-white font-semibold">{selectedBooking.bags ? 'Yes' : 'No'}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Payment</p>
                      <p className="text-white font-semibold">{selectedBooking.paymentMethod}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fare & Additional Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-green-600 to-green-700 p-6 rounded-lg">
                  <p className="text-green-100 text-sm mb-1">Estimated Fare</p>
                  <p className="text-white font-bold text-3xl">${selectedBooking.estimatedFare.toFixed(2)}</p>
                  <p className="text-green-100 text-xs mt-1">BSD</p>
                </div>
                
                <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
                  <p className="text-gray-400 text-sm mb-1">Created</p>
                  <p className="text-white font-semibold">
                    {new Date(selectedBooking.createdAt).toLocaleDateString()}
                  </p>
                  <p className="text-gray-300 text-sm">
                    {new Date(selectedBooking.createdAt).toLocaleTimeString()}
                  </p>
                </div>
              </div>

              {/* Special Notes */}
              {selectedBooking.specialRequests && (
                <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
                  <h3 className="text-white font-bold text-lg mb-3 flex items-center">
                    <MessageSquare className="w-5 h-5 text-cyan-400 mr-2" />
                    Special Notes
                  </h3>
                  <p className="text-gray-300">{selectedBooking.specialRequests}</p>
                </div>
              )}

              {/* Trip Progress Section */}
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-lg border-2 border-cyan-500">
                <h3 className="text-white font-bold text-xl mb-4 flex items-center">
                  <Clock className="w-6 h-6 text-cyan-400 mr-2" />
                  Trip Progress
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Current Status:</span>
                    <span className={`px-4 py-2 rounded-full text-sm font-bold border ${getStatusColor(selectedBooking.status)}`}>
                      {selectedBooking.status}
                    </span>
                  </div>
                  {selectedBooking.tripStartedAt && (
                    <div className="text-sm text-gray-400">
                      Trip started: {new Date(selectedBooking.tripStartedAt).toLocaleString()}
                    </div>
                  )}
                  {selectedBooking.completedAt && (
                    <div className="text-sm text-green-400">
                      Completed: {new Date(selectedBooking.completedAt).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>

              {/* Status-Specific Action Buttons */}
              <div className="flex flex-col gap-3 pt-4">
                <button
                  onClick={() => setSelectedBooking(null)}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-bold transition-all"
                >
                  Back to Dashboard
                </button>

                {/* Start Live Tracking Button - For Active Trips */}
                {['Driver Assigned', 'Driver En Route', 'Arrived at Pickup', 'Passenger Picked Up', 'En Route to Destination', 'Arrived at Destination'].includes(selectedBooking.status) && (
                  <a
                    href={`/driver-tracking?booking=${selectedBooking.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 text-center flex items-center justify-center gap-2"
                  >
                    <MapPin className="w-5 h-5" />
                    Start Live Tracking
                  </a>
                )}

                {/* Scheduled: Show Assign to Driver */}
                {selectedBooking.status === 'Scheduled' && (
                  <button
                    onClick={() => handleAssignDriver(selectedBooking.id)}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105"
                  >
                    Assign to Driver
                  </button>
                )}

                {/* Driver Assigned: Show Start Trip */}
                {selectedBooking.status === 'Driver Assigned' && (
                  <button
                    onClick={() => handleStartTrip(selectedBooking.id)}
                    className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105"
                  >
                    🚕 Start Trip
                  </button>
                )}

                {/* Driver En Route: Show Mark Arrived at Pickup */}
                {selectedBooking.status === 'Driver En Route' && (
                  <button
                    onClick={() => handleStatusTransition(selectedBooking.id, 'Arrived at Pickup')}
                    className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105"
                  >
                    📍 Mark Arrived at Pickup
                  </button>
                )}

                {/* Arrived at Pickup: Show Mark Passenger Picked Up */}
                {selectedBooking.status === 'Arrived at Pickup' && (
                  <button
                    onClick={() => handleStatusTransition(selectedBooking.id, 'Passenger Picked Up')}
                    className="bg-gradient-to-r from-indigo-500 to-cyan-500 hover:from-indigo-600 hover:to-cyan-600 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105"
                  >
                    👥 Mark Passenger Picked Up
                  </button>
                )}

                {/* Passenger Picked Up: Show Begin Route to Destination */}
                {selectedBooking.status === 'Passenger Picked Up' && (
                  <button
                    onClick={() => handleStatusTransition(selectedBooking.id, 'En Route to Destination')}
                    className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105"
                  >
                    🚗 Begin Route to Destination
                  </button>
                )}

                {/* En Route to Destination: Show Mark Arrived at Destination */}
                {selectedBooking.status === 'En Route to Destination' && (
                  <button
                    onClick={() => handleStatusTransition(selectedBooking.id, 'Arrived at Destination')}
                    className="bg-gradient-to-r from-teal-500 to-orange-500 hover:from-teal-600 hover:to-orange-600 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105"
                  >
                    🎯 Mark Arrived at Destination
                  </button>
                )}

                {/* Arrived at Destination: Show Complete Trip */}
                {selectedBooking.status === 'Arrived at Destination' && (
                  <button
                    onClick={() => handleStatusTransition(selectedBooking.id, 'Completed')}
                    className="bg-gradient-to-r from-orange-500 to-green-500 hover:from-orange-600 hover:to-green-600 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105"
                  >
                    ✅ Complete Trip
                  </button>
                )}

                {/* Completed: Show receipt actions */}
                {selectedBooking.status === 'Completed' && (
                  <>
                    {!selectedBooking.receiptStatus || selectedBooking.receiptStatus === 'pending' ? (
                      <button
                        onClick={() => handleGenerateReceipt(selectedBooking.id)}
                        className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 flex items-center justify-center space-x-2"
                      >
                        <FileText className="w-5 h-5" />
                        <span>Generate Receipt</span>
                      </button>
                    ) : selectedBooking.receiptStatus === 'generated' ? (
                      <button
                        onClick={() => handleSendReceipt(selectedBooking.id)}
                        className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 flex items-center justify-center space-x-2"
                      >
                        <Send className="w-5 h-5" />
                        <span>Send Receipt</span>
                      </button>
                    ) : (
                      <div className="bg-green-600 text-white px-6 py-4 rounded-lg font-bold text-lg text-center">
                        ✅ Trip Completed - Receipt Sent
                      </div>
                    )}
                  </>
                )}

                {/* Receipt Sent: Show status only */}
                {selectedBooking.status === 'Receipt Sent' && (
                  <div className="bg-emerald-600 text-white px-6 py-4 rounded-lg font-bold text-lg text-center">
                    📧 Receipt Sent
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverDashboard;
